import React from 'react';
import './Brand.css';

/*
 * Brand.js — the Bastion identity (WR-013 §1, reworked by WR-015 §3).
 *
 * ── Why the marks are inline SVG and solid ────────────────────────────
 *
 * The assets in public/brand/ draw the mark with `stroke` on `fill="none"` —
 * an outline. At sidebar size that renders as a thin oxblood line on
 * gunmetal, about 2.5:1, which is the invisibility WR-015 was raised to fix.
 *
 * So the in-app mark is drawn here instead: the same pentagon, SOLID and
 * filled with `currentColor`. The parent sets the colour per surface
 * (Brand.css): near-white on dark and oxblood on light in the sidebar.
 *
 * WR-017 adds a second variant. The solid mark stays the default and the
 * sidebar keeps it — that is still the WR-015 legibility fix, and 22px is
 * still where an outline drowns. The auth cards, at 64px, now render
 * `variant="outline"`: the brand's primary mark, stroked and never filled,
 * with the steel echo and the oxblood salient. Size is what makes the
 * difference safe, so the two variants are a size rule, not a preference.
 *
 * Inlining also keeps 7.7 KB of C2PA metadata per asset out of the bundle —
 * the four SVG files are ~8 KB each and almost entirely signature payload.
 *
 * The files in public/brand/ remain the source of truth for the geometry and
 * for icon surfaces (the favicon and PWA tile still use bastion-icon.svg,
 * which is already solid and carries its own gunmetal tile).
 */

// The Bastion mark (WR-016 §1).
//
// A five-point pentagon. WR-015 called this "wrong" and specified a
// seven-point bastion trace instead, which was implemented and shipped —
// but it rendered as an upward arrow rather than a star fort, and WR-016
// then settled the question: "the canonical mark is the pentagon as
// currently implemented (owner decision) … never its shape."
//
// So the trace is reverted and WR-015 §3's geometry instruction is dead.
// What survives from it is the part that actually fixed the reported bug:
// at sidebar size the mark is SOLID and takes currentColor. WR-017 scopes
// that to the sidebar rather than to every surface.
const PENTAGON = 'M40 4 L76 40 L60 84 L20 84 L4 40 Z';

// WR-017: the outline variant. Geometry is copied verbatim from
// public/brand/bastion-mark.svg — the echo pentagon (the rampart line) and
// the salient, the oxblood accent on only the two face segments meeting at
// the apex. Geometry is out of scope to change; these are transcriptions.
const ECHO = 'M40 16.6 L65.9 42.5 L54.4 74.2 L25.6 74.2 L14.1 42.5 Z';
const SALIENT = 'M21 23 L40 4 L59 23';

// Echo and salient are literals, not tokens, deliberately. WR-017 requires
// them "unchanged in both themes", and --bastion-steel is darkened in the
// light theme for contrast — so the token would drift where the brand says
// hold. These are the asset's own values.
const STEEL = '#5A6578';
const OXBLOOD = '#BA2B2E';

/**
 * The mark. Solid, inherits `currentColor`.
 *
 * `size` is the rendered height; width follows the 80:88 viewBox so the
 * proportions never distort.
 */
export function BastionMark({ size = 28, className = '', title = 'Bastion', variant = 'solid' }) {
  const outline = variant === 'outline';
  return (
    <svg
      className={`brand-mark ${outline ? 'brand-mark--outline' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      width={size * (80 / 88)}
      height={size}
      viewBox="0 0 80 88"
      fill="none"
      role="img"
      aria-label={title}
      focusable="false"
    >
      {outline ? (
        <>
          <path d={PENTAGON} stroke="currentColor" strokeWidth="2" strokeLinejoin="miter" />
          <path d={ECHO} stroke={STEEL} strokeWidth="1" strokeLinejoin="miter" />
          {/* The salient is the acceptance criterion: it must be present in
              every rendering of the outline mark, in both themes. */}
          <path d={SALIENT} stroke={OXBLOOD} strokeWidth="2" strokeLinejoin="miter" />
        </>
      ) : (
        <path d={PENTAGON} fill="currentColor" />
      )}
    </svg>
  );
}

/**
 * The full lockup: mark, wordmark, and the strapline under it.
 *
 * Composed here rather than loading bastion-lockup.svg, because that file
 * draws the same outline mark and pins its text colours to literals — so it
 * could not follow the theme. Built from live elements, every colour comes
 * from a token.
 */
export function BastionLockup({ className = '', markSize = 46, strapline = true, variant = 'solid' }) {
  return (
    <div className={`brand-lockup ${className}`.trim()}>
      <BastionMark size={markSize} variant={variant} className="brand-lockup-mark" />
      <div className="brand-lockup-text">
        <span className="brand-wordmark">Bastion</span>
        {/* WR-016 §3b had the auth cards pass strapline={false}. WR-017
            reverses that one point: the strapline IS the attribution on the
            lockup, so the auth cards now carry it and nothing else. The rest
            of §3b stands — header lockup, then the form, nothing between. */}
        {strapline && (
          <span className="brand-strapline">BST · KALKAN DEFENSE SYSTEMS</span>
        )}
      </div>
    </div>
  );
}

/**
 * "by Kalkan Defense Systems" — the formal attribution, for surfaces that
 * carry the name without the full lockup.
 */
export function BastionAttribution({ className = '' }) {
  return (
    <p className={`brand-attribution ${className}`.trim()}>
      by Kalkan Defense Systems
    </p>
  );
}

export default BastionMark;
